import React from 'react'
import Link from 'next/link'
import Image from 'next/image'

import { Badge } from '@wartek-id/badge'
import { Button } from '@wartek-id/button-v1'
import { Text } from '@wartek-id/text-v1'
import { Spinner } from '@wartek-id/spinner'
import { Icon } from '@wartek-id/icon-v2'

import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import KinerjaSidebar from 'app/KinerjaShared/components/KinerjaSidebar'
import { PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'
import { SimpleCard } from 'app/KinerjaPSPegawaiPage/components/SimpleCard'
import { SyncToEkin } from 'app/KinerjaPSPegawaiPage/components/SyncToEkin'
import { SyncPenetapanToEkin } from 'app/KinerjaPSPegawaiPage/components/SyncPenetapanToEkin'
import PlanningStatusBadge from 'app/KinerjaPSPegawaiPage/components/PlanningStatusBadge'
import PelaksanaanCard from 'app/KinerjaPSPegawaiPage/components/PelaksanaanCard'
import { usePSSummary } from 'app/KinerjaPSPegawaiPage/hooks/useSummary'
import ButtonsReset from 'app/KinerjaPSPegawaiPage/components/ButtonsReset'
import PenetapanResetButton from 'app/KinerjaPSPegawaiPage/components/PenetapanResetButton'
import {
  PANDUAN_PEJABAT_PENILAI_KINERJA_URL,
  PANDUAN_TAHAPAN_PERENCANAAN_KINERJA_URL,
  PANDUAN_TANPA_SEKOLAH_BINAAN_URL,
} from 'app/KinerjaPSPegawaiPage/constants/panduan'
import { usePPSTrackEvent } from 'app/KinerjaPSPegawaiPage/hooks/usePPSTrackEvent'
import CardPenetapanTahunanPS from 'app/KinerjaPSPegawaiPage/components/CardPenetapanTahunanResult'
import useToggleSdkContext from 'utils/useToogleSdk'

function PSPegawaiIndexPage() {
  const { trackEventBase } = usePPSTrackEvent()
  const { periodYear } = usePeriodContext()
  const toggle = useToggleSdkContext()
  const isPenetapanEnabled =
    toggle?.['kinerja-ps-pegawai-penetapan-enabled'] || false

  // Get Summary
  const { summary, summaryFetchStatus, isPelaksanaanState } = usePSSummary()
  const showPenetapanSection = React.useMemo(() => {
    if (!isPenetapanEnabled) {
      return false
    }

    return ['SENDING_TO_BKN', 'SENT_TO_BKN', 'ERROR_SEND'].includes(
      summary?.penetapan?.status
    )
  }, [isPenetapanEnabled, summary?.penetapan?.status])

  React.useEffect(() => {
    trackEventBase('beranda', 'view')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="pl-64">
      <KinerjaSidebar sticky activeMenu="ps-pegawai" />
      <div className="w-full gap-4 p-6">
        {summaryFetchStatus === 'loading' && (
          <div className="flex justify-center">
            <Spinner data-testid="loading-summary" />
          </div>
        )}
        {['success', 'error'].includes(summaryFetchStatus) && (
          <div className="space-y-8">
            {showPenetapanSection && (
              <div className="space-y-4">
                <Text variant="heading-sm">
                  Lihat hasil Penilaian Kinerja Tahunan
                </Text>
                <div className="items-start w-full gap-4 space-y-4 xl:flex xl:space-y-0 flex-nowrap">
                  <div className="grow">
                    <CardPenetapanTahunanPS
                      year={periodYear}
                      status={summary?.penetapan?.status}
                    />
                  </div>
                  <SyncPenetapanToEkin
                    isError={summaryFetchStatus === 'error'}
                    summaryData={summary}
                  />
                </div>
              </div>
            )}

            <div className="w-full space-y-4">
              <Text variant="heading-sm">
                Lihat dokumen Perencanaan dan Pelaksanaan
              </Text>
              <div className="w-full gap-4 space-y-4 xl:flex xl:space-y-0">
                <div className="grow">
                  {summaryFetchStatus === 'success' &&
                    summary?.sekolahBinaanCount === 0 && (
                      <SimpleCard className="flex flex-1 p-4 pb-6 gap-9 h-fit">
                        <div className="flex-none">
                          <Image
                            src="/images/errors/img-no-access.png"
                            alt=""
                            width={64}
                            height={64}
                          />
                        </div>

                        <div className="flex flex-col gap-2">
                          <Text variant="heading-md">
                            Pengelolaan Kinerja hanya dapat dilakukan oleh
                            Pengawas yang memiliki satuan pendidikan binaan
                          </Text>
                          <Text>
                            Mohon periksa kembali data satdik binaan Anda di
                            SIMTENDIK.
                          </Text>
                          <a
                            href={PANDUAN_TANPA_SEKOLAH_BINAAN_URL}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <Text variant="body-sm-bold" color="informational">
                              Cek selengkapnya
                            </Text>
                          </a>
                        </div>
                      </SimpleCard>
                    )}

                  {((summary?.sekolahBinaanCount > 0 &&
                    Boolean(summary?.isRhkAtasanExists)) ||
                    summary?.isRhkAtasanExists === undefined) && (
                    <div className="flex-1 space-y-4">
                      <SimpleCard className="flex flex-row items-center justify-between px-4 py-6 h-fit">
                        <div
                          data-testid="perencanaan-entry-point"
                          className="flex flex-col gap-2"
                        >
                          <Text variant="heading-md" className="mb-2">
                            Perencanaan
                          </Text>

                          {/** Show only on "Perencanaan" states */}
                          <PlanningStatusBadge
                            separateRevisionStates
                            state={summary?.perencanaanStatus}
                          />
                        </div>

                        <div>
                          <Link
                            href={`${PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH}/perencanaan${
                              isPelaksanaanState ? '/rangkuman' : '/beranda'
                            }`}
                            passHref
                          >
                            <Button
                              as="a"
                              size="lg"
                              color="white"
                              className="ml-auto"
                            >
                              Cek
                            </Button>
                          </Link>
                        </div>
                      </SimpleCard>

                      {isPelaksanaanState && (
                        <>
                          <SimpleCard className="flex flex-col gap-2 p-3 h-fit">
                            <PelaksanaanCard
                              data-testid="praktik-kinerja-card"
                              imageSrc="/illustrations/img-interactive-activity.png"
                              title="Praktik Kinerja"
                              state={
                                summary?.pelaksanaanStatuses?.praktikKinerja
                              }
                              isAppealInProgress={Boolean(
                                summary?.pelaksanaanStatuses
                                  ?.praktikKinerjaPerubahanStatus
                              )}
                              href={`${PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH}/pelaksanaan/praktik-kinerja`}
                            />
                            <PelaksanaanCard
                              imageSrc="/illustrations/img-reflection-documents-magnifying-glass.png"
                              title="Perilaku Kerja"
                              state={
                                summary?.pelaksanaanStatuses?.perilakuKerja
                              }
                              href={`${PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH}/pelaksanaan/perilaku-kerja`}
                            />
                            <PelaksanaanCard
                              imageSrc="/illustrations/img-flow-process.png"
                              title="Pengembangan Kompetensi"
                              state={
                                summary?.pelaksanaanStatuses
                                  ?.pengembanganKompetensi
                              }
                              href={`${PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH}/pelaksanaan/pengembangan-kompetensi`}
                            />
                            <PelaksanaanCard
                              imageSrc="/illustrations/img-book-rapor-graphic-doc.png"
                              title="Pelaksanaan Tugas"
                              state={
                                summary?.pelaksanaanStatuses
                                  ?.dokumenAkuntabilitas
                              }
                              href={`${PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH}/pelaksanaan/pelaksanaan-tugas`}
                            />
                          </SimpleCard>

                          {!showPenetapanSection && (
                            <SimpleCard className="flex items-center justify-between gap-2 px-6 py-4 h-fit">
                              <div className="space-y-2">
                                <Text variant="heading-md">
                                  Hasil Penilaian Kinerja
                                </Text>

                                <Text variant="body-sm" color="subdued">
                                  Berdasarkan Rating Praktik Kinerja dan Rating
                                  Perilaku Kerja Anda, dengan mempertimbangkan
                                  kegiatan Pengembangan Kompetensi.
                                </Text>
                              </div>
                              <div className="flex-none">
                                <Badge>Tersedia di akhir periode</Badge>
                              </div>
                            </SimpleCard>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {summaryFetchStatus === 'success' &&
                    summary.sekolahBinaanCount > 0 &&
                    !summary?.isRhkAtasanExists && (
                      <SimpleCard className="flex flex-1 p-4 pb-6 gap-9 h-fit">
                        <div className="flex-none">
                          <Image
                            src="/images/errors/img-no-access.png"
                            alt=""
                            width={64}
                            height={64}
                          />
                        </div>

                        <div className="flex flex-col gap-2">
                          <Text variant="heading-md">
                            Perencanaan baru dapat dimulai setelah Sasaran
                            Kinerja Pegawai (SKP) Pejabat Penilai Kinerja
                            tersedia di E-Kinerja
                          </Text>
                          <Text>
                            Mohon tunggu beberapa hari atau kabari Pejabat
                            Penilai Kinerja.
                          </Text>
                          <a
                            href={PANDUAN_TAHAPAN_PERENCANAAN_KINERJA_URL}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <Text variant="body-sm-bold" color="informational">
                              Cek selengkapnya
                            </Text>
                          </a>
                        </div>
                      </SimpleCard>
                    )}
                </div>

                <div className="space-y-5 w-96">
                  <SyncToEkin
                    isError={summaryFetchStatus === 'error'}
                    perencanaanStatus={summary?.perencanaanStatus}
                    ekinSyncErrorMessage={summary?.ekinSyncErrorMessage}
                    ekinSyncStatus={summary?.ekinSyncStatus}
                  />

                  {summary?.sekolahBinaanCount > 0 && (
                    <SimpleCard className="flex flex-col gap-4 p-6 w-96">
                      <div className="flex flex-col gap-1">
                        <Text variant="heading-sm">
                          Pejabat Penilai Kinerja Anda
                        </Text>
                        <Text>
                          {summary?.pejabatPenilaiKinerja ||
                            'Tidak ditemukan. Mohon hubungi Dinas Pendidikan setempat.'}
                        </Text>
                      </div>

                      <Text color="subdued" className="flex gap-2">
                        <Icon color="inherit">info_outlined</Icon>

                        <span>
                          <span className="font-bold">Tim Kinerja</span> juga
                          akan hadir untuk membantu Pejabat Penilai Kinerja.
                        </span>
                      </Text>

                      <a
                        href={PANDUAN_PEJABAT_PENILAI_KINERJA_URL}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <Text variant="body-sm-bold" color="informational">
                          Cek selengkapnya
                        </Text>
                      </a>
                    </SimpleCard>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Display the Reset button only if the toggle is enabled */}
      <ButtonsReset />

      <PenetapanResetButton pengawasSekolahId={summary?.pengawasSekolahId} />
    </div>
  )
}

export default PSPegawaiIndexPage
